import { useCallback, useEffect, useRef, useState } from "react";
import { FormControl, IconButton, ListItemText, MenuItem, Select } from "@mui/material";
import ArrowDropDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import ArrowLeft from '@/assets/ArrowLeft'
import ArrowRight from '@/assets/ArrowRight'
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid2 from "@mui/material/Grid2";
import chunk from "lodash/chunk";
import uniq from "lodash/uniq";
import useOnboardingContext from "@/hooks/useOnboardingContext";
import FontList from "@/components/font-list";
import { FONTS } from "@/constants/onboarding";
import SelectListButton from "@/components/SelectListButton";

import useIsMobile from '../../../../hooks/useIsMobile'

const ARROW_ICON_ENABLED = '#1A1A1A'
const ARROW_ICON_DISABLED = '#808080'
const FONT_TEXT_COLOR = '#1A1A1A'

const fontPagerArrowIconButtonSx = {
  p: '4px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&.Mui-disabled': {
    opacity: 1,
  },
}

const fontPagerArrowRightIconButtonSx = {
  ...fontPagerArrowIconButtonSx,
  pr: '8px',
}

const CustomFontsIcon = ({ className }) => (
  <svg
    width="18"
    height="19"
    viewBox="0 0 18 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M12 7.25C12 7.66475 11.6648 8 11.25 8H9.75V12.5C9.75 12.9148 9.41475 13.25 9 13.25C8.58525 13.25 8.25 12.9148 8.25 12.5V8H6.75C6.33525 8 6 7.66475 6 7.25C6 6.83525 6.33525 6.5 6.75 6.5H11.25C11.6648 6.5 12 6.83525 12 7.25ZM15 13.277V5.723C13.8525 5.558 12.942 4.6475 12.777 3.5H5.223C5.058 4.6475 4.1475 5.558 3 5.723V13.277C4.1475 13.442 5.058 14.3525 5.223 15.5H12.777C12.942 14.3525 13.8525 13.442 15 13.277ZM18 15.875C18 17.3225 16.8225 18.5 15.375 18.5C14.3303 18.5 13.4258 17.8857 13.0035 17H4.9965C4.57425 17.8857 3.66975 18.5 2.625 18.5C1.1775 18.5 0 17.3225 0 15.875C0 14.8302 0.61425 13.9257 1.5 13.5035V5.4965C0.61425 5.07425 0 4.16975 0 3.125C0 1.6775 1.1775 0.5 2.625 0.5C3.66975 0.5 4.57425 1.11425 4.9965 2H13.0043C13.4265 1.11425 14.331 0.5 15.3758 0.5C16.8233 0.5 18.0008 1.6775 18.0008 3.125C18.0008 4.16975 17.3865 5.07425 16.5008 5.4965V13.5043C17.3865 13.9265 18.0008 14.831 18.0008 15.8757L18 15.875Z"
      fill={FONT_TEXT_COLOR}
    />
  </svg>
);

const WizardStep3 = () => {
  const { selectedFont, customFont, setCustomFont, setSelectedFont, templateStyle, selectedTemplate } =
    useOnboardingContext();
  const [page, setPage] = useState(0);
  const templateVersion = selectedTemplate?.version
  const fonts = chunk(FONTS[templateStyle][templateVersion], 6);
  const { isMobile } = useIsMobile()
  const containerRef = useRef(null)

  const getUniqueFonts = useCallback(() => {
    const dropDownFonts = [];
    FONTS[templateStyle][templateVersion]?.forEach((font) => {
      dropDownFonts.push(font.header);
      dropDownFonts.push(font.body);
    });

    return uniq(dropDownFonts).sort((a, b) => a.localeCompare(b));
  }, [templateStyle, templateVersion]);

  const pages = Math.ceil(fonts.length);

  const onFontChange = useCallback((font) => {
    setSelectedFont(font);
    if (!font.custom) setCustomFont()
  }, []);

  useEffect(() => {
    if (!selectedFont && fonts.length > 0 && fonts[0].length > 0) {
      onFontChange(fonts[0][0]);
    }
  }, [selectedFont, fonts, onFontChange]);

  useEffect(() => {
    if (customFont) {
      onFontChange({ ...customFont, custom: true });
      window.scrollBy({ top: containerRef.current.scrollHeight, behavior: 'smooth'  })
    }
  }, [customFont, onFontChange]);
  return (
    <Box ref={containerRef}>
      <Box
        className="flex items-center justify-between gap-3"
      >
        <Typography variant="h5" sx={{ color: FONT_TEXT_COLOR, mb: '8px' }}>
          Recommended fonts
        </Typography>
        <Box className="flex shrink-0 gap-3 mb-[8px]">
          <IconButton
            aria-label="Previous page"
            size="small"
            disableRipple
            onClick={() => setPage((prev) => prev - 1)}
            disabled={page === 0}
            sx={fontPagerArrowIconButtonSx}
          >
            <ArrowLeft
              fill={page === 0 ? ARROW_ICON_DISABLED : ARROW_ICON_ENABLED}
            />
          </IconButton>
          <IconButton
            aria-label="Next page"
            size="small"
            disableRipple
            disabled={page >= pages - 1}
            onClick={() => setPage((prev) => prev + 1)}
            sx={fontPagerArrowRightIconButtonSx}
          >
            <ArrowRight
              fill={
                page >= pages - 1
                  ? ARROW_ICON_DISABLED
                  : ARROW_ICON_ENABLED
              }
            />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ mt: "0px" }}>
        <FontList
          items={fonts}
          selected={selectedFont}
          onChange={onFontChange}
          page={page}
        />
      </Box>
      { }
      <Typography
        variant="h5"
        sx={{ color: FONT_TEXT_COLOR, mt: 2, mb: "8px" }}
      >
        Custom font
      </Typography>
      <Grid2 container spacing={2} size={12}>
        <Grid2 size={isMobile? 12: 6}>
          <SelectListButton
            className="!py-3"
            sx={(theme) => ({
              outline: 'none !important',
              borderRadius: '10px',
              boxSizing: 'border-box',
              backgroundColor: '#F5F5F5',
              // Keep 1px border in all states so size does not change; add a second ring
              // with box-shadow (no layout effect) to match the previous 2px look on hover/select
              border: '1px dashed #DBDBDB',
              boxShadow: 'none',
              '&:hover': {
                outline: 'none !important',
                border: `1px solid ${theme.palette.secondary.main}`,
                backgroundColor: '#CFF3FD',
                borderColor: '#50CFF1',
                boxShadow: '0 0 0 1px #50CFF1',
              },
              '&.Mui-selected': {
                outline: 'none !important',
                border: `1px solid ${theme.palette.secondary.main}`,
                backgroundColor: '#CFF3FD',
                borderColor: '#50CFF1',
                boxShadow: '0 0 0 1px #50CFF1',
              },
            })}
            selected={selectedFont?.custom}
            onClick={() => {
              const font = selectedFont || fonts[0][0];
              setCustomFont(font);
              onFontChange({ ...font, custom: true });
            }}
          >
            {customFont ? (
              <>
                <ListItemText
                  className='max-w-[90%]'
                  primary={customFont?.header.split(',')[0]}
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: customFont?.header.split(',')[0],
                      fontWeight: `${customFont?.header.split(',')[0] === 'Corben' ? 'normal' : 'bold'}`,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      color: FONT_TEXT_COLOR,
                    },
                  }}
                  secondaryTypographyProps={{
                    sx: {
                      fontFamily: customFont?.body.split(',')[0],
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      color: FONT_TEXT_COLOR,
                    }
                  }}
                  secondary={customFont?.body.split(',')[0]}
                />
              </>
            ) : (
              <Box
                textAlign="center"
                width="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <CustomFontsIcon className='mb-2' />
                <Typography
                  variant="h6"
                  sx={{
                    textDecoration: "underline",
                    color: FONT_TEXT_COLOR,
                  }}
                >
                  Select your own fonts
                </Typography>
              </Box>
            )}
          </SelectListButton>
        </Grid2>
      </Grid2>
      {customFont && (
        <>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              pt: 4,
              pb: 2.5,
              alignItems: "center",
              borderBottom: "1px solid #C0C4CF",
            }}
          >
            <Typography sx={{ width: 100,
            fontSize: {
              xs: "0.75rem",
              sm: "1rem"
            },
            fontWeight: 500,
            color: FONT_TEXT_COLOR,
            }}>Heading</Typography>
            <FormControl variant="standard" sx={{ mx: 1, minWidth: 120 }}>
              <Select
                IconComponent={() => (
                  <ArrowDropDownOutlinedIcon sx={{ width: { xs: "12px", sm: "16px" }}}/>
                )}
                sx={{
                  fontSize: {
                    xs: "13px",
                    sm: "1rem"
                  },
                  fontWeight: 500,
                  color: FONT_TEXT_COLOR,
                  width: "fit-content",
                  '& .MuiSelect-select': {
                    paddingRight: '12px !important'
                  }
                }}
                value={customFont.header}
                MenuProps={{
                  sx: {
                    borderRadius: 0
                  }
                }}
                disableUnderline
                onChange={({ target: { value } }) => {
                  setCustomFont((prev) => ({ ...prev, header: value }));
                }}
                label="Heading"
                name="custom-font-heading"
                id="custom-font-heading"
              >
                {getUniqueFonts().map((font, i) => (
                  <MenuItem
                    key={`heading-${font}-${i}`}
                    value={font}
                    sx={{ fontFamily: font.split(',')[0], color: FONT_TEXT_COLOR }}
                  >
                    {font.split(',')[0]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              py: 2.5,
              alignItems: "center",
            }}
          >
            <Typography sx={{ width: 100, fontSize: {
              xs: "0.75rem",
              sm: "1rem"
            },
            fontWeight: 500,
            color: FONT_TEXT_COLOR,
            }}>Body text</Typography>
            <FormControl variant="standard" sx={{ mx: 1, minWidth: 120 }}>
              <Select
                IconComponent={() => (
                  <ArrowDropDownOutlinedIcon sx={{ width: { xs: "12px", sm: "16px" }}}/>
                )}
                sx={{
                  fontSize: {
                    xs: "13px",
                    sm: "1rem"
                  },
                  color: FONT_TEXT_COLOR,
                  width: "fit-content",
                  '& .MuiSelect-select': {
                    paddingRight: '12px !important'
                  }
                }}
                value={customFont.body || ""}
                MenuProps={{
                  sx: {
                    borderRadius: 0
                  }
                }}
                disableUnderline
                onChange={({ target: { value } }) => {
                  setCustomFont((prev) => ({ ...prev, body: value }));
                }}
                label="Body"
                name="custom-font-body"
                id="custom-font-body"
              >
                {getUniqueFonts().map((font, i) => (
                  <MenuItem
                    key={`body-${font}-${i}`}
                    value={font}
                    sx={{ fontFamily: font.split(',')[0], color: FONT_TEXT_COLOR }}
                  >
                    {font.split(',')[0]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </>
      )}
    </Box>
  );
};

export default WizardStep3;
